import { Directive } from '@angular/core';

import { V12RetailFinance } from './v12-retail-finance.namespace';

import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { V12RetailFinanceService } from './v12-retail-finance.service';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class V12RetailFinanceComponentDef extends AbstractCheckoutPayment<V12RetailFinance.IPaymentMethod> {
    info?: V12RetailFinance.IInfo;

    private _depositOptionValue?: number;

    private _packOptionId?: string;

    protected readonly methodCode = V12RetailFinance.PaymentMethod.VFinance;

    @NgInject(TimeoutService)
    private timeoutService: TimeoutService;

    @NgInject(V12RetailFinanceService)
    private v12RetailFinanceService: V12RetailFinanceService;

    private onDataUpdateThrottled: () => void;

    ngOnInit(): void {
        super.ngOnInit();
        this.onDataUpdateThrottled = this.timeoutService.throttle(() => this.onDataUpdate(), 200);
    }

    ////////////////////////////////////////////
    ////////
    ////////    Script methods
    ////////
    ////////////////////////////////////////////

    protected onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        this.onDataUpdate();
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.PaymentMethodsUpdate).subscribe(() => this.onDataUpdateThrottled()));
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => this.onDataUpdateThrottled()));
        this.subscriptions.add(this.app.customer.basket.subscribe(() => this.onDataUpdateThrottled()));
        this.subscriptions.add(this.checkoutStepComponent.submittedSubject.subscribe(() => this.detectChanges()));
    }

    protected getJsAssets(): string[] {
        return [];
    }

    protected preload(): boolean {
        return true;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Deposit
    ////////
    ////////////////////////////////////////////

    get depositOptionValue(): number | undefined {
        return this._depositOptionValue;
    }

    set depositOptionValue(value: number | undefined) {
        this._depositOptionValue = typeof value === 'number' ? value : parseFloat(value);
        this.updateInfo();
        this.detectChanges();
    }

    get depositOptions(): V12RetailFinance.IDepositOption[] {
        return this.v12RetailFinanceService.getAvailableDepositOptions();
    }

    get depositOption(): V12RetailFinance.IDepositOption | undefined {
        if (typeof this.depositOptionValue !== 'undefined') {
            return this.depositOptions.find(i => i.value === this.depositOptionValue);
        }
        return undefined;
    }

    trackByDepositValue(index: number, depositOption: V12RetailFinance.IDepositOption): number {
        return depositOption.value;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Package Plan
    ////////
    ////////////////////////////////////////////

    get packOptionId(): string | undefined {
        return this._packOptionId;
    }

    set packOptionId(value: string | undefined) {
        this._packOptionId = value;
        this.updateInfo();
        this.detectChanges();
    }

    get packOptions(): V12RetailFinance.IPackOption[] {
        if (this.depositOption) {
            return this.v12RetailFinanceService.getAvailablePacksForDeposit(this.depositOption);
        }
        return [];
    }

    get packOption(): V12RetailFinance.IPackOption | undefined {
        if (this.packOptionId) {
            return this.packOptions.find(i => i.id === this.packOptionId);
        }
        return undefined;
    }

    trackByPackId(index: number, packOption: V12RetailFinance.IPackOption): string {
        return packOption.id;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Data update
    ////////
    ////////////////////////////////////////////

    private onDataUpdate(): void {
        if (!this.depositOptionValue || !this.depositOptions.find(i => i.value === this.depositOptionValue)) {
            this.depositOptionValue = this.depositOptions[0]?.value;
        }

        if (this.packOptionId && !this.packOptions.find(i => i.id === this.packOptionId)) {
            this.packOptionId = undefined;
        }
        this.updateInfo();
        this.detectChanges();
    }

    ////////////////////////////////////////////
    ////////
    ////////    Info
    ////////
    ////////////////////////////////////////////

    private updateInfo(): void {
        this.info = this.getInfo();
    }

    private getInfo(): V12RetailFinance.IInfo | undefined {
        const depositOption = this.depositOption;
        const packOption = this.packOption;
        if (depositOption && packOption) {
            const productName = packOption.title;
            const totalAmount = this.v12RetailFinanceService.total;
            const depositAmount = (totalAmount / 100) * depositOption.value;
            const loanAmount = (totalAmount * 100 - depositAmount * 100) / 100;
            const interestRate = packOption.apr;
            const monthlyPayment = Math.round(loanAmount * packOption.interest * 100) / 100;
            const terms = packOption.months;
            const totalAmountPayable = interestRate === 0 ? loanAmount : monthlyPayment * terms;
            const creditAmount = Math.abs(totalAmountPayable * 100 - loanAmount * 100) / 100;
            return {
                productName,
                totalAmount,
                depositAmount,
                loanAmount,
                interestRate,
                monthlyPayment,
                terms,
                creditAmount,
                totalAmountPayable
            };
        }
        return undefined;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Checkout
    ////////
    ////////////////////////////////////////////

    private getPaymentData(): V12RetailFinance.IPaymentSubmissionData | undefined {
        const depositOption = this.depositOption;
        if (depositOption) {
            const packOption = this.packOption;
            if (packOption) {
                return {
                    method: this.methodCode,
                    additional_data: {
                        v12Percent: depositOption.value.toString(),
                        v12Pack: packOption.id
                    }
                };
            }
        }
        return undefined;
    }

    /**
     * Abstract method letting you plug in your action before checkout submission
     * @returns {false | void} False if can't continue
     */
    protected onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        const paymentSubmissionData = this.getPaymentData();
        if (paymentSubmissionData) {
            checkoutSubmitBeforeEventData.data.paymentMethod.additional_data = paymentSubmissionData.additional_data;
            checkoutSubmitBeforeEventData.data.paymentMethod.method = paymentSubmissionData.method;
        } else {
            checkoutSubmitBeforeEventData.continueSubmission = false;
        }
    }
}
